import { Button, Kind } from '@thanx/uikit/button'
import { Radio, RadioGroup } from '@thanx/uikit/radio'
import { Text } from '@thanx/uikit/text'
import {
  getOrderingMerchantConfig,
  GET_ORDERING_MERCHANT_CONFIG_SUCCESS,
  updateOrderingMerchantConfig,
  UPDATE_ORDERING_MERCHANT_CONFIG_SUCCESS,
} from 'actions/config'
import { alert } from 'actions/flash'
import EditNavbar from 'components/Navbar/EditNavbar'
import PopoverInfo from 'components/PopoverInfo'
import Section from 'components/Section'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import {
  OrderingConfigApiFields,
  UnavailableItemVisibilitySetting,
} from 'models/Config'
import React, { useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import CmsPage from 'scenes/Cms/components/CmsPage'
import OrderingNotEnabledPage from '../OrderingNotEnabledPage'

const t = buildTranslate('cms.settings.unavailable_item_visibility')

function getDisplayValue(
  setting: UnavailableItemVisibilitySetting | undefined
): string {
  if (setting === UnavailableItemVisibilitySetting.HIDDEN) {
    return t('options.hidden')
  }
  return t('options.disabled')
}

const UnavailableItemVisibility: React.FC<{ showTitle?: boolean }> = ({
  showTitle = true,
}) => {
  const merchant = useCurrentMerchant()
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [setting, setSetting] = useState<
    UnavailableItemVisibilitySetting | undefined
  >(undefined)
  const [editSetting, setEditSetting] = useState<
    UnavailableItemVisibilitySetting | undefined
  >(undefined)

  const displayAlert = useCallback(
    (key: 'danger' | 'success', messageKey: string) => {
      dispatch(
        alert({
          key,
          message: t(messageKey),
          timeout: 5,
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    const run = async () => {
      setIsLoading(true)
      try {
        const response = await dispatch(getOrderingMerchantConfig())

        if (response.type === GET_ORDERING_MERCHANT_CONFIG_SUCCESS) {
          const orderingConfig: OrderingConfigApiFields =
            response.payload.data.ordering_config
          if (orderingConfig) {
            setSetting(orderingConfig.unavailable_item_visibility)
          }
        } else {
          displayAlert('danger', 'fetch_error')
        }
      } finally {
        setIsLoading(false)
      }
    }

    run()
  }, [dispatch, displayAlert])

  const handleEdit = () => {
    setEditSetting(setting)
    setIsEditing(true)
  }

  const handleExitWithoutSaving = () => {
    setEditSetting(undefined)
    setIsEditing(false)
  }

  const handlePublish = async () => {
    setIsSubmitting(true)

    if (!editSetting) {
      displayAlert('danger', 'submit_error')
      setIsSubmitting(false)
    } else {
      const response = await dispatch(updateOrderingMerchantConfig(editSetting))
      if (response.type === UPDATE_ORDERING_MERCHANT_CONFIG_SUCCESS) {
        setSetting(editSetting)
        displayAlert('success', 'submit_success')
        setIsEditing(false)
        setEditSetting(undefined)
      } else {
        displayAlert('danger', 'submit_error')
      }
      setIsSubmitting(false)
    }
  }

  if (!merchant?.ordering_enabled) {
    return <OrderingNotEnabledPage />
  }

  if (isEditing) {
    return (
      <div className="h-100">
        {ReactDOM.createPortal(
          <EditNavbar
            title={
              <div>
                <Text.SmallCaps3 tag="div" color="white" className="mb-xxs">
                  {t('configuration')}
                </Text.SmallCaps3>
                <Text.Header4 className="my-0" color="white">
                  {t('title')}
                </Text.Header4>
              </div>
            }
          >
            <div>
              <Button
                kind={Kind.SECONDARY_REVERSE}
                className="mr-s"
                onClick={handleExitWithoutSaving}
              >
                {t('exit_without_saving')}
              </Button>
              <Button
                kind={Kind.PRIMARY_REVERSE}
                onClick={handlePublish}
                disabled={isSubmitting}
              >
                {t('publish_changes')}
              </Button>
            </div>
          </EditNavbar>,
          document.body
        )}
        <Section className="p-m">
          <div className="mb-m">
            <span className="bold font-size-14">{t('field_label')}</span>
            <PopoverInfo className="ml-xs" text={t('description')} />
          </div>
          <RadioGroup
            value={editSetting}
            onChange={value =>
              setEditSetting(value as UnavailableItemVisibilitySetting)
            }
          >
            <div className="mb-m">
              <Radio
                value={UnavailableItemVisibilitySetting.DISABLED}
                checked={
                  editSetting === UnavailableItemVisibilitySetting.DISABLED
                }
              >
                {t('options.disabled')}
              </Radio>
              <p className="body-text-4 grey-70 ml-l mt-xxs">
                {t('options.disabled_explanation')}
              </p>
            </div>
            <div>
              <Radio
                value={UnavailableItemVisibilitySetting.HIDDEN}
                checked={
                  editSetting === UnavailableItemVisibilitySetting.HIDDEN
                }
              >
                {t('options.hidden')}
              </Radio>
              <p className="body-text-4 grey-70 ml-l mt-xxs">
                {t('options.hidden_explanation')}
              </p>
            </div>
          </RadioGroup>
        </Section>
      </div>
    )
  }

  return (
    <CmsPage
      showTitle={showTitle}
      title={t('title')}
      description={t('description')}
      isLoading={isLoading}
    >
      <Section className="p-m">
        <div className="d-flex align-items-center justify-content-between mb-m">
          <Text.Header4 className="my-0">{t('section_title')}</Text.Header4>
          <Button kind={Kind.MINIMAL} onClick={handleEdit}>
            {t('edit')}
          </Button>
        </div>
        <div>
          <p className="bold font-size-14 mb-0">
            {t('field_label')}
            <PopoverInfo className="ml-xs" text={t('description')} />
          </p>
          <p className="grey-70 mt-xs">{getDisplayValue(setting)}</p>
        </div>
      </Section>
    </CmsPage>
  )
}

export default UnavailableItemVisibility
